// Außenkontur der L×B-Fläche eines Teils — engine-frei, in zwei Darstellungen.
// Ergebnis in TEIL-LOKALEN Koordinaten [[x, z], …] (x = Länge, z = Breite,
// Ursprung = Teil-Mittelpunkt), Umlauf im Uhrzeigersinn in (x, z) — das ist
// gegen den Uhrzeigersinn in der Skizzen-Ebene (x, −z), in der Renderer und
// STEP-Export die Fläche aufspannen.
//
// Quellen der Kontur, in dieser Reihenfolge:
//   1. meta.outline — freie Kontur eines Skizzen-Formteils (bereits [[x, z], …])
//   2. meta.corners — Fase (Sehne) oder Radius (Bogen) an den vier Ecken
//   3. sonst das schlichte Rechteck L×B
//
// Zwei Darstellungen derselben Kontur:
//   outlinePolygon(part)  → [[x, z], …] — Punktliste, Bögen als Sehnenzug
//       ausgerollt. Das ist die Form für den Renderer (render/csg.js baut damit
//       Manifold-Meshes, die ohnehin tesselliert sein müssen).
//   outlineSegments(part) → [{ kind: "line"|"arc", … }, …] — dieselbe Kontur
//       mit EXAKTEN Bögen. Das ist die Form für den STEP-Export, wo aus einem
//       Viertelkreis auch eine echte Kreiskante werden soll (Fräsbahnen!).
//
// Segmentliste im Detail — der Umlauf ist geschlossen, Startpunkt ist das
// `to` des LETZTEN Segments (genau wie die Punktliste implizit schließt):
//   { kind: "line", to: [x, z] }
//   { kind: "arc",  to: [x, z], radius, center: [cx, cz], clockwise }
//       `clockwise` = Umlaufsinn des Bogens in (x, z); zusammen mit `center`
//       und dem Startpunkt ist der Bogen damit eindeutig (Radius ist
//       redundant, aber praktisch für Prüfungen und den Bau).
// Invariante: besteht die Kontur nur aus Strecken, gilt
// `outlineSegments(part).map((s) => s.to)` === `outlinePolygon(part)`.
import { cornersOf } from "./corners"

const ARC_SEGMENTS = 16

// Kreis-Erkennung in freien Konturen: ein Skizzen-Kreis liegt als 32-Eck in
// meta.outline. Wir erkennen ihn an der Geometrie (alle Punkte gleich weit vom
// Schwerpunkt, gleichmäßige Winkelschritte) statt uns auf ein Merkmal im meta
// zu verlassen — so werden auch BEREITS GESPEICHERTE Projekte exakt exportiert,
// ohne Migration. Die Toleranzen decken die 0,1-mm-Rundung ab, mit der
// sketch.js die Konturpunkte ablegt.
//
// Genauigkeit: durch eben diese 0,1-mm-Rundung bleibt im gefitteten Radius ein
// Rest von rund 0,03 mm. Der Sehnenzug aus 32 Punkten liegt dagegen ~0,6 mm
// daneben (Pfeilhöhe) — der Bogen ist also gut 20-mal genauer und vor allem
// eine echte Kreiskante für die Fräsbahn.
const CIRCLE_MIN_POINTS = 12
const CIRCLE_RADIUS_TOLERANCE_MM = 0.15
const CIRCLE_RADIUS_TOLERANCE_REL = 0.002
const CIRCLE_ANGLE_TOLERANCE_REL = 0.2

// Liefert die freie Kontur eines Formteils oder null (auch bei Datenmüll)
export function freeOutline(part) {
  const raw = part?.meta?.outline
  if (!Array.isArray(raw)) return null
  const points = []
  for (const point of raw) {
    if (!Array.isArray(point) || point.length < 2) return null
    const x = Number(point[0])
    const z = Number(point[1])
    if (!Number.isFinite(x) || !Number.isFinite(z)) return null
    points.push([x, z])
  }
  const cleaned = withoutDuplicates(points)
  return cleaned.length >= 3 ? ensureWinding(cleaned) : null
}

// Doppelte Punkte (auch der wiederholte Schlusspunkt) killen jede Vernähung
// zu einem Umriss — der Umlauf ist implizit geschlossen.
function withoutDuplicates(points) {
  return points.filter((point, index) => {
    const previous = points[(index - 1 + points.length) % points.length]
    return point[0] !== previous[0] || point[1] !== previous[1]
  })
}

// Umlauf im Uhrzeigersinn in (x, z) erzwingen (= CCW in der Skizzen-Ebene),
// damit die Fläche immer in dieselbe Richtung zeigt
function ensureWinding(points) {
  let area = 0
  for (let i = 0; i < points.length; i++) {
    const [x1, z1] = points[i]
    const [x2, z2] = points[(i + 1) % points.length]
    area += x1 * z2 - x2 * z1
  }
  return area > 0 ? [...points].reverse() : points
}

// true, wenn die Kontur vom Rechteck abweicht (freie Kontur oder Eckbearbeitung)
export function hasShapedOutline(part) {
  if (freeOutline(part)) return true
  return Object.keys(cornersOf(part)).length > 0
}

// Ecken-Geometrie des Rechtecks: je Ecke Ein- und Auslaufpunkt der Bearbeitung
// sowie der eingerückte Bogenmittelpunkt. Umlauf hinten-links → hinten-rechts →
// vorn-rechts → vorn-links; dPrev/dNext = Richtung der ein-/auslaufenden Kante.
// Ohne Bearbeitung fallen entry, exit und center auf die Ecke zusammen.
function cornerGeometry(part) {
  const hx = part.length_mm / 2
  const hz = part.width_mm / 2
  const corners = cornersOf(part)
  const specs = [
    { key: "hl", x: -hx, z: hz, dPrev: [0, 1], dNext: [1, 0] },
    { key: "hr", x: hx, z: hz, dPrev: [1, 0], dNext: [0, -1] },
    { key: "vr", x: hx, z: -hz, dPrev: [0, -1], dNext: [-1, 0] },
    { key: "vl", x: -hx, z: -hz, dPrev: [-1, 0], dNext: [0, 1] }
  ]

  return specs.map((spec) => {
    const treatment = corners[spec.key]
    const size = treatment?.size_mm ?? 0
    const entry = [spec.x - spec.dPrev[0] * size, spec.z - spec.dPrev[1] * size]
    const exit = [spec.x + spec.dNext[0] * size, spec.z + spec.dNext[1] * size]
    const center = [entry[0] + spec.dNext[0] * size, entry[1] + spec.dNext[1] * size]
    return { treatment, size, entry, exit, center }
  })
}

export function outlinePolygon(part) {
  const free = freeOutline(part)
  if (free) return free

  const points = []
  for (const { treatment, size, entry, exit, center } of cornerGeometry(part)) {
    points.push(entry)
    if (!treatment) continue
    if (treatment.type === "chamfer") {
      points.push(exit)
      continue
    }
    // Radius: Viertelbogen um den eingerückten Mittelpunkt, im Uhrzeigersinn
    const [cx, cz] = center
    const a0 = Math.atan2(entry[1] - cz, entry[0] - cx)
    let a1 = Math.atan2(exit[1] - cz, exit[0] - cx)
    if (a1 > a0) a1 -= Math.PI * 2
    for (let i = 1; i <= ARC_SEGMENTS; i++) {
      const angle = a0 + ((a1 - a0) * i) / ARC_SEGMENTS
      points.push([cx + Math.cos(angle) * size, cz + Math.sin(angle) * size])
    }
  }
  return points
}

// Dieselbe Kontur mit exakten Bögen — siehe Modulkopf für das Format.
export function outlineSegments(part) {
  const free = freeOutline(part)
  if (free) return freeSegments(free)

  const segments = []
  const lineTo = (to) => {
    const previous = segments[segments.length - 1]?.to
    if (previous && previous[0] === to[0] && previous[1] === to[1]) return
    segments.push({ kind: "line", to })
  }

  for (const { treatment, size, entry, exit, center } of cornerGeometry(part)) {
    lineTo(entry)
    if (!treatment || size <= 0) continue
    if (treatment.type === "chamfer") {
      lineTo(exit)
      continue
    }
    segments.push({ kind: "arc", to: exit, radius: size, center, clockwise: true })
  }
  return segments
}

// Freie Kontur: entweder ein erkannter Kreis (→ zwei Halbkreis-Bögen) oder
// der unveränderte Polygonzug.
function freeSegments(points) {
  const circle = circleFromPoints(points)
  if (circle) return circleSegments(circle)
  return points.map((point) => ({ kind: "line", to: point }))
}

// Erkennt eine Punktfolge als Kreis-Tessellierung und liefert Mittelpunkt,
// Radius, Startwinkel und Umlaufsinn — oder null.
function circleFromPoints(points) {
  const n = points.length
  if (n < CIRCLE_MIN_POINTS) return null

  const cx = points.reduce((sum, [x]) => sum + x, 0) / n
  const cz = points.reduce((sum, [, z]) => sum + z, 0) / n
  const radii = points.map(([x, z]) => Math.hypot(x - cx, z - cz))
  const radius = radii.reduce((sum, r) => sum + r, 0) / n
  if (!(radius > 0)) return null

  const radiusTolerance = Math.max(CIRCLE_RADIUS_TOLERANCE_MM, radius * CIRCLE_RADIUS_TOLERANCE_REL)
  if (radii.some((r) => Math.abs(r - radius) > radiusTolerance)) return null

  const angles = points.map(([x, z]) => Math.atan2(z - cz, x - cx))
  const step = (Math.PI * 2) / n
  const angleTolerance = step * CIRCLE_ANGLE_TOLERANCE_REL
  let sweep = 0
  for (let i = 0; i < n; i++) {
    const delta = wrapAngle(angles[(i + 1) % n] - angles[i])
    if (Math.abs(Math.abs(delta) - step) > angleTolerance) return null
    sweep += delta
  }
  // Ein voller Umlauf muss herauskommen — sonst ist es kein geschlossener Kreis
  if (Math.abs(Math.abs(sweep) - Math.PI * 2) > step) return null

  return { center: [cx, cz], radius, startAngle: angles[n - 1], clockwise: sweep < 0 }
}

// Winkeldifferenz auf (−π, π] normieren
function wrapAngle(angle) {
  let value = angle
  while (value > Math.PI) value -= Math.PI * 2
  while (value <= -Math.PI) value += Math.PI * 2
  return value
}

// Kreis als zwei Halbkreis-Bögen. Start ist der letzte Punkt der Punktliste,
// damit die Segmentliste denselben Startpunkt hat wie das Polygon. Die
// Endpunkte werden aus dem gefitteten Kreis neu gerechnet und liegen damit
// exakt (statt auf 0,1 mm gerundet) auf der Kreisbahn.
function circleSegments({ center, radius, startAngle, clockwise }) {
  const [cx, cz] = center
  const at = (angle) => [cx + Math.cos(angle) * radius, cz + Math.sin(angle) * radius]
  const half = clockwise ? -Math.PI : Math.PI
  const arc = (to) => ({ kind: "arc", to, radius, center: [cx, cz], clockwise })
  return [arc(at(startAngle + half)), arc(at(startAngle))]
}
